/**
 * Loomia API client (P1-T11).
 *
 * - Base URL from `VITE_API_URL` (default http://localhost:3000). The backend
 *   mounts routers at the root (e.g. `/auth/me`), so no `/api` prefix here.
 * - Tokens live in localStorage (`loomia.*`). No secrets are ever logged.
 * - On 401 the client tries one silent refresh-token rotation, then gives up
 *   and fires `onUnauthorized` (the app shell redirects to #/login).
 * - API errors always have shape { error, message } (see api/src/auth/routes.ts).
 */
import type {
  AiJob,
  AuthResponse,
  AuthTokens,
  Category,
  CreditPricingConfig,
  CreditTx,
  DailyRewardClaimResult,
  DailyRewardStatus,
  JobListPage,
  SubmitJobBody,
  Template,
  TemplateListPage,
  TemplateListQuery,
  UploadFileMeta,
  UploadRequestBody,
  UploadRequestResponse,
  User,
} from '@loomia/shared';

const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ||
  'http://localhost:3000';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

const LS_ACCESS = 'loomia.access_token';
const LS_REFRESH = 'loomia.refresh_token';

export function loadTokens(): StoredTokens | null {
  try {
    const accessToken = localStorage.getItem(LS_ACCESS);
    const refreshToken = localStorage.getItem(LS_REFRESH);
    return accessToken && refreshToken ? { accessToken, refreshToken } : null;
  } catch {
    return null;
  }
}

export function saveTokens(tokens: AuthTokens): void {
  localStorage.setItem(LS_ACCESS, tokens.accessToken);
  localStorage.setItem(LS_REFRESH, tokens.refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Attach the Bearer access token (default false). */
  auth?: boolean;
}

class ApiClient {
  /** Fired when the session is unrecoverable (bad/expired refresh token). */
  onUnauthorized: (() => void) | null = null;

  private refreshing: Promise<boolean> | null = null;

  private async raw(
    path: string,
    method: string,
    body: unknown,
    token: string | null,
  ): Promise<Response> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private tryRefresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    const attempt = this.doRefresh();
    this.refreshing = attempt;
    void attempt.then(() => {
      if (this.refreshing === attempt) this.refreshing = null;
    });
    return attempt;
  }

  private async doRefresh(): Promise<boolean> {
    const tokens = loadTokens();
    if (!tokens) return false;
    try {
      const res = await this.raw('/auth/refresh', 'POST', { refreshToken: tokens.refreshToken }, null);
      if (!res.ok) {
        clearTokens();
        return false;
      }
      const data = (await res.json()) as AuthResponse;
      saveTokens(data.tokens);
      return true;
    } catch {
      clearTokens();
      return false;
    }
  }

  private failUnauthorized(): never {
    clearTokens();
    this.onUnauthorized?.();
    throw new ApiError(401, 'unauthorized', 'Session expired. Please sign in again.');
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method ?? 'GET';
    const needsAuth = opts.auth ?? false;

    let token = needsAuth ? loadTokens()?.accessToken ?? null : null;
    let res = await this.raw(path, method, opts.body, token);

    if (res.status === 401 && needsAuth) {
      const refreshed = await this.tryRefresh();
      if (!refreshed) this.failUnauthorized();
      token = loadTokens()?.accessToken ?? null;
      res = await this.raw(path, method, opts.body, token);
      if (res.status === 401) this.failUnauthorized();
    }

    if (res.status === 204) return undefined as T;

    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      // Non-JSON body (shouldn't happen with this API, but stay safe).
    }

    if (!res.ok) {
      const body = (data ?? {}) as { error?: string; message?: string };
      throw new ApiError(
        res.status,
        typeof body.error === 'string' ? body.error : 'request_failed',
        typeof body.message === 'string' ? body.message : `Request failed (HTTP ${res.status}).`,
      );
    }
    return data as T;
  }

  // -- Auth ---------------------------------------------------------------

  /** `devMagicUrl` is only present when the API runs in dev link mode. */
  requestMagicLink(email: string): Promise<{ ok: boolean; devMagicUrl?: string }> {
    return this.request('/auth/magic-link/request', {
      method: 'POST',
      body: { email },
    });
  }

  /** `guestToken`: the current anonymous session, so its uploads move to the account. */
  async verifyMagicLink(
    token: string,
    guestToken?: string,
    attribution?: Record<string, string | boolean>,
  ): Promise<AuthResponse> {
    const res = await this.request<AuthResponse>('/auth/magic-link/verify', {
      method: 'POST',
      body: { token, ...(guestToken ? { guestToken } : {}), ...(attribution ? { attribution } : {}) },
    });
    saveTokens(res.tokens);
    return res;
  }

  /** Where the browser goes to start an OAuth sign-in (full-page redirect). */
  oauthStartUrl(provider: 'google'): string {
    return `${API_BASE}/auth/oauth/${provider}`;
  }

  /** Anonymous session: lets people upload before signing in (no credits). */
  async createGuest(attribution?: Record<string, string | boolean>): Promise<AuthResponse> {
    const res = await this.request<AuthResponse>('/auth/guest', {
      method: 'POST',
      body: attribution ? { attribution } : {},
    });
    saveTokens(res.tokens);
    return res;
  }

  async logout(): Promise<void> {
    const tokens = loadTokens();
    try {
      if (tokens) {
        await this.request('/auth/logout', {
          method: 'POST',
          body: { refreshToken: tokens.refreshToken },
        });
      }
    } finally {
      clearTokens();
    }
  }

  me(): Promise<{ user: User }> {
    return this.request('/auth/me', { auth: true });
  }

  deleteAccount(): Promise<{ ok: boolean; deletedAt: string }> {
    return this.request('/auth/account', { method: 'DELETE', auth: true });
  }

  // -- Credits ------------------------------------------------------------

  getBalance(): Promise<{ balance: number }> {
    return this.request('/credits/balance', { auth: true });
  }

  getCreditHistory(limit = 30): Promise<{ transactions: CreditTx[]; nextCursor: string | null }> {
    return this.request(`/credits/history?limit=${limit}`, { auth: true });
  }

  getPricing(): Promise<CreditPricingConfig> {
    return this.request('/credits/pricing');
  }

  /** Server verifies a Google Play purchase with Google, then grants credits. */
  playPurchase(
    productId: string,
    purchaseToken: string,
  ): Promise<{ status: 'granted' | 'pending'; credits: number; balance: number }> {
    return this.request('/billing/play/purchase', { method: 'POST', auth: true, body: { productId, purchaseToken } });
  }

  // -- Daily reward (P3-T01) ---------------------------------------------

  getDailyReward(): Promise<DailyRewardStatus> {
    return this.request('/rewards/daily', { auth: true });
  }

  claimDailyReward(): Promise<DailyRewardClaimResult> {
    return this.request('/rewards/daily/claim', { method: 'POST', auth: true });
  }

  // -- Templates ----------------------------------------------------------

  listTemplates(query: TemplateListQuery = {}): Promise<TemplateListPage> {
    const params = new URLSearchParams();
    if (query.category) params.set('category', query.category);
    if (query.tool) params.set('tool', query.tool);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.cursor) params.set('cursor', query.cursor);
    const suffix = params.toString();
    return this.request(`/templates${suffix ? `?${suffix}` : ''}`);
  }

  /** API wraps the template as `{ template }`. */
  async getTemplate(slug: string): Promise<Template> {
    const res = await this.request<{ template: Template }>(`/templates/${encodeURIComponent(slug)}`);
    return res.template;
  }

  /** Counts a detail-page view for the Trending tab. Best effort: never throws. */
  recordTemplateView(slug: string): void {
    this.request(`/templates/${encodeURIComponent(slug)}/view`, { method: 'POST' }).catch(() => undefined);
  }

  /** API wraps the list as `{ categories }`. */
  async listCategories(): Promise<Category[]> {
    const res = await this.request<{ categories: Category[] }>('/categories');
    return res.categories;
  }

  // -- Uploads (P1-T04) -----------------------------------------------------

  requestUpload(body: UploadRequestBody): Promise<UploadRequestResponse> {
    return this.request('/uploads/request', { method: 'POST', body, auth: true });
  }

  /**
   * PUT raw image bytes to the URL from `requestUpload`.
   *
   * Uses XHR (fetch has no upload-progress events). Attaches the Bearer
   * token when the URL points at our own API (local driver); presigned S3
   * URLs carry their own auth and must NOT get the header.
   */
  putUploadBytes(
    url: string,
    file: Blob,
    headers: Record<string, string> | undefined,
    onProgress?: (ratio: number) => void,
  ): Promise<void> {
    let needsAuth = false;
    try {
      needsAuth = new URL(url, window.location.href).origin === new URL(API_BASE).origin;
    } catch {
      needsAuth = false;
    }
    const token = needsAuth ? loadTokens()?.accessToken ?? null : null;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      for (const [k, v] of Object.entries(headers ?? {})) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new ApiError(xhr.status, 'upload_failed', `Upload failed (HTTP ${xhr.status}).`));
      };
      xhr.onerror = () => reject(new TypeError('network'));
      xhr.onabort = () => reject(new ApiError(0, 'upload_aborted', 'Upload was cancelled.'));
      xhr.send(file);
    });
  }

  completeUpload(fileId: string): Promise<UploadFileMeta> {
    return this.request(`/uploads/${encodeURIComponent(fileId)}/complete`, {
      method: 'POST',
      auth: true,
    });
  }

  /** Fetch processed-file metadata (incl. signed read URLs). */
  getUpload(fileId: string): Promise<UploadFileMeta> {
    return this.request(`/uploads/${encodeURIComponent(fileId)}`, { auth: true });
  }

  deleteUpload(fileId: string): Promise<void> {
    return this.request(`/uploads/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
      auth: true,
    });
  }

  // -- AI jobs (P1-T05) -----------------------------------------------------

  submitJob(body: SubmitJobBody): Promise<{ job: AiJob; idempotent: boolean }> {
    return this.request('/ai/jobs', { method: 'POST', body, auth: true });
  }

  reportJob(jobId: string, reason: string): Promise<{ reported: boolean }> {
    return this.request(`/ai/jobs/${encodeURIComponent(jobId)}/report`, { method: 'POST', body: { reason }, auth: true });
  }

  getJob(jobId: string): Promise<{ job: AiJob }> {
    return this.request(`/ai/jobs/${encodeURIComponent(jobId)}`, { auth: true });
  }

  /** Paginated job history (P1-T16). Server has no media-kind filter, so the
   *  Collection screen filters client-side after resolving result uploads. */
  listJobs(opts?: { limit?: number; cursor?: string }): Promise<JobListPage> {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    return this.request(`/ai/jobs${qs ? `?${qs}` : ''}`, { auth: true });
  }
}

export const api = new ApiClient();
